import { useSearchParams } from "react-router-dom";
import { LEVELS, MEASURES } from "./aabiConstants";
import { getDefaultDateRange, isValidRange } from "./aabiPeriods";

// The single parsed AABI scope (Stage 1 UI upgrade §9.2). Every URL param is
// validated/normalized here at the boundary so components never re-parse or
// second-guess the URL. `level` is derived state, validated against the IDs —
// never a second source of truth.
//
// Scope model (parent plan D1 + Stage 1 UI upgrade §8.3):
//   manufacturer -> category -> group -> product (final scope).
// Drilling up drops all deeper keys; impossible combinations are normalized.

const DATE_KEYS = ["start", "end"];
const SCOPE_KEYS = ["level", "categoryId", "groupId", "productId"];
const MEASURE_KEY = "measure";

// Non-negative integer strings only; "012" is normalized to 12.
const parseId = (raw) => {
  if (raw === null || raw === undefined) return null;
  const str = String(raw).trim();
  if (!/^\d+$/.test(str)) return null;
  const parsed = Number(str);
  return Number.isSafeInteger(parsed) && parsed > 0 ? String(parsed) : null;
};

export const parseAabiScope = (searchParams) => {
  const defaults = getDefaultDateRange();

  const categoryId = parseId(searchParams.get("categoryId"));
  const groupId = parseId(searchParams.get("groupId"));
  const productId = parseId(searchParams.get("productId"));

  // Normalize impossible combinations: a deeper key without its parent is
  // dropped (§8.3 "reject or normalize impossible combinations").
  const validGroupId = categoryId ? groupId : null;
  const validProductId =
    categoryId && validGroupId ? productId : null;

  const level = !categoryId
    ? LEVELS.MANUFACTURER
    : !validGroupId
      ? LEVELS.CATEGORY
      : validProductId
        ? LEVELS.PRODUCT
        : LEVELS.GROUP;

  const rawStart = searchParams.get("start");
  const rawEnd = searchParams.get("end");
  // Both or neither: a lone start/end is not an applied range.
  const hasValidRange =
    rawStart && rawEnd && isValidRange({ startDate: rawStart, endDate: rawEnd });
  const startDate = hasValidRange ? rawStart : defaults.startDate;
  const endDate = hasValidRange ? rawEnd : defaults.endDate;

  const measure =
    searchParams.get("measure") === MEASURES.REALIZED
      ? MEASURES.REALIZED
      : MEASURES.CURRENT;

  return { level, categoryId, groupId: validGroupId, productId: validProductId, startDate, endDate, measure };
};

// True when the URL carries no non-default analytics state (defaults are
// implied, not frozen into the URL — §5.3 reset behavior).
const isDefaultUrlState = (searchParams) =>
  ![...DATE_KEYS, ...SCOPE_KEYS].some((key) => searchParams.has(key)) &&
  searchParams.get(MEASURE_KEY) !== MEASURES.REALIZED;

export const useAabiDrillState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const scope = parseAabiScope(searchParams);

  // Writes scope + period + measure, deleting default-valued keys instead of
  // freezing them into the URL (§5.3). Deeper scope keys are always dropped
  // when navigating up (§7.1).
  const applyUpdates = (updates) => {
    const next = parseAabiScope(searchParams);

    const nextCategoryId =
      "categoryId" in updates ? updates.categoryId : next.categoryId;
    const nextGroupId = "groupId" in updates ? updates.groupId : next.groupId;
    const nextProductId =
      "productId" in updates ? updates.productId : next.productId;
    const nextMeasure =
      "measure" in updates ? updates.measure : next.measure;
    const nextRange = {
      startDate: "startDate" in updates ? updates.startDate : next.startDate,
      endDate: "endDate" in updates ? updates.endDate : next.endDate,
    };

    const params = new URLSearchParams();
    if (nextCategoryId) params.set("categoryId", String(nextCategoryId));
    if (nextGroupId) params.set("groupId", String(nextGroupId));
    if (nextProductId) params.set("productId", String(nextProductId));
    if (nextMeasure === MEASURES.REALIZED) {
      // `current` is the default measure — only persist the non-default.
      params.set("measure", nextMeasure);
    }
    const defaults = getDefaultDateRange();
    const isDefaultRange =
      nextRange.startDate === defaults.startDate &&
      nextRange.endDate === defaults.endDate;
    if (!isDefaultRange) {
      params.set("start", nextRange.startDate);
      params.set("end", nextRange.endDate);
    }
    // `level` is derived; never write it back (§8.3).

    setSearchParams(params, { replace: false });
  };

  return {
    ...scope,
    isDefaultUrl: isDefaultUrlState(searchParams),
    setMeasure: (nextMeasure) => applyUpdates({ measure: nextMeasure }),
    setDateRange: ({ startDate: nextStart, endDate: nextEnd }) =>
      applyUpdates({ startDate: nextStart, endDate: nextEnd }),
    // Reset (§5.3): return to root, YTD and Current Sales by deleting every
    // analytics query key — the URL returns to its default form rather than
    // freezing today's YTD dates into it.
    reset: () => {
      const params = new URLSearchParams(searchParams);
      [...DATE_KEYS, ...SCOPE_KEYS, MEASURE_KEY].forEach((key) =>
        params.delete(key),
      );
      setSearchParams(params, { replace: false });
    },
    drillInto: (row) => {
      // null-id rows ("Uncategorized" / "Ungrouped" / "Unassigned product") are not scopes.
      if (row?.id == null) return;
      if (row?.isLeaf) {
        // Product rows select the final product scope (§3.3 recommendation).
        applyUpdates({ productId: row.id });
        return;
      }
      if (scope.level === LEVELS.MANUFACTURER) {
        applyUpdates({ categoryId: row?.id, groupId: null, productId: null });
      } else if (scope.level === LEVELS.CATEGORY) {
        applyUpdates({ groupId: row?.id, productId: null });
      }
    },
    goToManufacturer: () =>
      applyUpdates({ categoryId: null, groupId: null, productId: null }),
    goToCategory: () => applyUpdates({ groupId: null, productId: null }),
    goToGroup: () => applyUpdates({ productId: null }),
  };
};
